/**
 * useCompanyExport Hook
 * Custom hook for exporting companies to Excel
 */

import { useState, useCallback } from 'react';
import { companyService } from '../services/companyService';
import { CompanyFilters } from '../types/company';
import { useSnackbar } from 'notistack';

interface UseCompanyExportResult {
  exporting: boolean;
  exportCompanies: (filters?: Partial<CompanyFilters>, filename?: string) => Promise<void>;
}

export const useCompanyExport = (): UseCompanyExportResult => {
  const { enqueueSnackbar } = useSnackbar();

  const [exporting, setExporting] = useState<boolean>(false);

  const exportCompanies = useCallback(async (
    filters?: Partial<CompanyFilters>,
    filename = 'empresas.xlsx'
  ): Promise<void> => {
    try {
      setExporting(true);

      const blob = await companyService.exportCompanies(filters);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Empresas exportadas com sucesso', { variant: 'success' });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao exportar empresas';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setExporting(false);
    }
  }, [enqueueSnackbar]);

  return {
    exporting,
    exportCompanies
  };
};
